import { Button } from '@/components/ui/button';
import { PlusIcon } from '@radix-ui/react-icons';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import CopyButton from '@/components/ui/copy-to-clipboard/copy-button';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useCreateLLMApiKey } from '@/service/llm/api-key/hook';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import { toastSomethingError } from '@/components/ui/toast-content';
import { useGetQuotaLimit } from '@/service/quota-limit/hook';
import { API_KEY_STATUS_CODE_RESULT } from '@/service/llm/api-key/constants';
import { toast } from 'sonner';

const CreateAPIKey = () => {
  const { workspaceId } = useParamWorkspaceId();
  const quotaLimit = useGetQuotaLimit();
  const { data, mutate, isPending } = useCreateLLMApiKey({
    onSuccess: (data) => {
      if (data?.code === API_KEY_STATUS_CODE_RESULT.EXCEED_LIMIT) {
        toast.error('Quota limit reached');
        return;
      }
      form.reset();
      setIsSuccess(true);
    },
    onError: (error) => {
      toastSomethingError();
    },
  });

  const [isSuccess, setIsSuccess] = useState(false);
  const apiKey = data?.LLM_api_key || '';

  const formSchema = z.object({
    name: z.string().max(50).optional(),
  });
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
    },
  });

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    mutate({
      workspace_id: workspaceId,
      api_key_name: data.name,
    });
  };

  return (
    <Dialog>
      <DialogTrigger asChild onClick={() => setIsSuccess(false)}>
        <Button variant="secondary" startIcon={<PlusIcon />}>
          Generate new API key
        </Button>
      </DialogTrigger>
      <DialogContent>
        {isSuccess ? (
          <>
            <DialogHeader>
              <DialogTitle>Save your key</DialogTitle>
              <DialogDescription>
                Please save this API key somewhere safe and accessible.
              </DialogDescription>
              <div className="flex w-full items-center space-x-2 py-4">
                <Input value={apiKey} />
                <CopyButton text={apiKey} />
              </div>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant={'secondary'}>Done</Button>
              </DialogClose>
            </DialogFooter>
          </>
        ) : (
          <>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <DialogHeader>
                  <DialogTitle>Generate new API key</DialogTitle>
                  {/* <DialogDescription>
                    Create a new API key to access the API.
                  </DialogDescription> */}
                  <DialogDescription>
                    Quota limit Remaining: {quotaLimit?.data?.llm_api_key.using}/
                    {quotaLimit?.data?.llm_api_key.max}
                  </DialogDescription>
                </DialogHeader>
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="py-4">
                      <FormLabel>
                        Name{' '}
                        <span className="text-xs font-normal text-muted-foreground">
                          Optional
                        </span>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Name Key" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant={'outline'}>Cancel</Button>
                  </DialogClose>
                  <Button type="submit" disabled={isPending}>
                    Create api key
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateAPIKey;
